import { useState, useEffect, useRef, FC, memo, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { InputAdornment } from '@mui/material';

import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
const autocompleteService = { current: null as any };


const loadGooglePlacesAPI = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) return resolve();

    // Re-use existing script tag if present
    const existing = document.getElementById('google-places-api') as HTMLScriptElement | null;
    if (existing) {
      if (window.google?.maps?.places) return resolve();
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Places API')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-places-api';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&loading=async&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Places API'));
    document.head.appendChild(script);
  });
};

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
  isInitial?: boolean;
  index?: number;
  types?: string[];
  place_id?: string;
}

interface Props {
  mobile?: boolean;
  callback?: any;
  mobileText?: any;
  setMobileText?: any;
  setSelectedCity?: any;
  setShowCities?: any;
  showCities?: boolean;
  city?: any;
  width?: any;
  showShrinkText?: any;
  showIcon?: boolean;
  iconSx?: object;
  mapOnPage?: boolean;
  labelSx?: object;  
  inputSx?: object;
}

const GoogleMaps: FC<Props> = ({
  mobile = false,
  callback,
  mobileText,
  setMobileText,
  setSelectedCity,
  setShowCities,
  showCities,
  city,
  width,
  showShrinkText,
  showIcon = false,
  iconSx,
  mapOnPage = false,
  labelSx,
  inputSx
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [requestedLoad, setRequestedLoad] = useState(false);

  const triggerLoadIfNeeded = useCallback(() => {
    if (isLoaded || requestedLoad || loadError) return;
    setRequestedLoad(true);
    loadGooglePlacesAPI()
      .then(() => setIsLoaded(true))
      .catch((error) => setLoadError(error));
  }, [isLoaded, requestedLoad, loadError]);

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const search = useSelector((state: any) => state.searchReducer.search);
  // (Removed unused 'loaded' ref)
  const [focused, setFocused] = useState(false);
  const lastInputValue = useRef('');

  // Memoize initial states to prevent re-creation on every render
  const initialStates = useMemo(() => [
    { 
      isInitial: true,
      index: 0,
      description: 'San Diego, CA, USA',
      structured_formatting: {
        main_text: "San Diego",
        secondary_text: 'CA, USA'
      },
      types: ['locality']
    },
    {
      isInitial: true,
      index: 1,
      description: 'Asheville, NC, USA',
      structured_formatting: {
        main_text: "Asheville",
        secondary_text: 'NC, USA'
      },
      types: ['locality']
    },
    {
      isInitial: true,
      index: 2,
      description: 'Austin, TX, USA',
      structured_formatting: {
        main_text: "Austin",
        secondary_text: 'TX, USA'
      },
      types: ['locality']
    },
    {
      isInitial: true,
      index: 3,
      description: 'Seattle, WA, USA',
      structured_formatting: {
        main_text: "Seattle",
        secondary_text: 'WA, USA'
      },
      types: ['locality']
    },
    {
      isInitial: true,
      index: 4,
      description: 'Santa Fe, NM, USA',
      structured_formatting: {
        main_text: "Santa Fe",
        secondary_text: 'NM, USA'
      },
      types: ['locality']
    },
  ], []);

  const handleLocationChange = (newValue: PlaceType | null) => {
    if (mobile && !newValue && setMobileText) {
      setMobileText(newValue);
    }
    if (!newValue) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ 'address': newValue.description }, function (results, status) {
      if (status === google.maps.GeocoderStatus.OK && results && results[0] && setSelectedCity) {
        setSelectedCity({
          city: newValue,
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });
      } else {
        console.log("Geocode was not successful for the following reason: " + status);
      }
    });
  };

  useEffect(() => {
    if (mobile && mobileText) {
      setInputValue(mobileText);      
    }
  }, [mobile, mobileText]);

  useEffect(() => {
    if (!autocompleteService.current && (window as any).google?.maps?.places) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
    }

    if (!autocompleteService.current) {
      return;
    }

    if (inputValue === '') {
      setOptions(initialStates);
      lastInputValue.current = '';
      return;
    }

    if (inputValue === lastInputValue.current) {
      return;
    }

    lastInputValue.current = inputValue;

    autocompleteService.current.getPlacePredictions(
      { input: inputValue.trim() },
      function(results: any) {
        if (inputValue !== lastInputValue.current) {
          return;
        }

        let newOptions: readonly PlaceType[] = [];
        if (results) {
          const filtered = results.filter(
            (r) => r.structured_formatting && r.structured_formatting.secondary_text
          );
          newOptions = [...filtered];
          
          if (mobile && callback && typeof callback === 'function') {
            callback(filtered);
          }
        }
        setOptions(newOptions.length > 0 ? newOptions : initialStates);
      }
    );
  }, [inputValue, mobile, callback, initialStates]);

  useEffect(() => {
    const fieldset = document.getElementsByTagName('fieldset');
    for (const set of fieldset) {
      set.setAttribute('style', 'border: none;');
    }
    setOptions(initialStates);
  }, [initialStates]);
  
  // Show initial options immediately; load API only after user intent.
  
  if (mobile) {
    return (
      <Autocomplete
        filterOptions={(x) => x}
        forcePopupIcon={false}
        clearIcon={<></>}
        options={options}
        open={false}  
        value={city ? city?.city : search.city}

        isOptionEqualToValue={(option, value) => {
      
          if (!option && !value) return true;
          if (!option || !value) return false;
          
          if (option === value) return true;

          if (typeof option === 'string' && typeof value === 'string') {
            return option === value;
          }
          
       
          if (option.description && value.description) {
            return option.description === value.description;
          }
          
         
          if (option.place_id && value.place_id) {
            return option.place_id === value.place_id;
          }

          return false;
        }}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : (option.description || '')
        }
        onChange={(event: any, newValue: PlaceType | null) => {
            handleLocationChange(newValue);
        }}
        onInputChange={(event, newInputValue) => {
             setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField 
            {...params} 
            placeholder="Going to..." 
            fullWidth 
            variant='outlined' 
            size="small" 
            sx={{ 
              borderRadius: 5,
              '& .MuiOutlinedInput-root': {
                border: 'none',
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none',
                },
              }
            }}
            onClick={() => {
              triggerLoadIfNeeded();
              if (setShowCities) {
                setShowCities(true);
              }
            }}
            InputProps={{
              ...params.InputProps,
              readOnly: true,
              onFocus: (e) => {
                // Prevent focus to avoid keyboard on mobile
                e.target.blur();
              }
            }}
          />
        )}
      />
    )
  }

  return (
    <Autocomplete
      id="google-map-input"
      forcePopupIcon={false}
      sx={{
        "& .MuiAutocomplete-input": {
         color: 'black', 
         fontSize: '15px',
         pl: '0 !important',
         fontFamily: 'Lato',
         'cursor': 'pointer'
        },
        "& .MuiInputBase-root": { 
          width: (width || 280), 
          // position: 'relative',
          height: '40px',
          border: focused ? 'none' : '1px solid white', 
          boxShadow: (focused && !showShrinkText) ? 5 : 0,
          background: 'white', 
          'cursor': 'pointer'
     
        },
        'cursor': 'pointer'
      }}
      componentsProps={{
        paper: {
          sx: {
            width: 400,
            whiteSpace: 'nowrap'
          },
        },
        popper: {
          placement: 'bottom-start',

        },
      }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      clearIcon={(focused && inputValue) ? <HighlightOffIcon /> : <></>}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={city ? city?.city : search.city}
  noOptionsText={loadError ? 'Error loading service' : (!isLoaded && requestedLoad ? 'Loading locations...' : 'No locations')}
  onOpen={() => { setFocused(true); triggerLoadIfNeeded(); }}
      onClose={() => setFocused(false)}
      onChange={(event: any, newValue: PlaceType | null) => handleLocationChange(newValue)}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={showShrinkText ? "LOCATION" : ""}
          sx={{ ml: '0rem', mt: showShrinkText ? '0.5rem': 0 }}
          InputProps={{
            ...params.InputProps,
            disableUnderline: showShrinkText ? true : false,
            sx: inputSx || {},
            
            startAdornment: ((focused || showShrinkText) && !showIcon) ? <></> : <><InputAdornment position="start"><LocationOnIcon sx={{ color: 'black', ...iconSx }} /></InputAdornment></>
          }}
          placeholder="Going to..."
          fullWidth 
          size="small" 
          variant={showShrinkText ? "standard" : "outlined"}
          InputLabelProps={{
            shrink: true,
            sx: labelSx || {}
          }} 
        />
      )}
      groupBy={(option) => option.isInitial}
      renderGroup={(params) => {
        return (
          <>
            {params.group && 
              <li key={'list-header-text'}>
                <Box my="0.5rem">
                    <Typography variant="base" ml="1rem" sx={{ fontWeight: 800}}>Where in the United States are you traveling?</Typography>
                </Box>
              </li>
            }
            {params.children}
          </>
        )
      }}
      renderOption={(props, option) => {  
        return (
          <li {...props} key={option.place_id || option.description} >
            <Grid container alignItems="center" my="0.20rem">
              <Grid item sx={{ display: 'flex', width: '1.75rem' }}>
                <LocationOnIcon sx={{ fontSize: '1.25rem' }} />
              </Grid>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
              
                <Box
                  component="span"
                  sx={{ fontFamily: 'Lato', fontSize: '1rem', color: 'black' }}
                >
                  {option.structured_formatting.main_text.length > 45 ? `${option.structured_formatting.main_text.slice(0,45)}...` :  option.structured_formatting.main_text} 
                </Box>
           
                <Typography style={{ fontSize: '0.75rem' }}>
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}

export default memo(GoogleMaps)